import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import axiosInstance from '../../api/axiosInstance';
import { useTheme } from '../../contexts/ThemeContext';
import './Header.css';

const Header: React.FC = () => {
  const { user, canAccessMenu, refetch } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isLogin = !!user;

  // 특정 경로가 현재 활성화되었는지 체크
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const getNavItemClass = (path: string, extraClass: string = '') => {
    return `nav-item ${isActive(path) ? 'active' : ''} ${extraClass}`.trim();
  };

  return (
    <header className="main-header">
      <div className="header-container">
        <div className="logo-wrapper">
          <Link to="/" className="logo-link" onClick={() => setIsMobileMenuOpen(false)}>역사 아카이브</Link>
        </div>
        
        {/* 모바일 햄버거 버튼 */}
        <button 
          className="hamburger-btn" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="메뉴 토글"
        >
          {isMobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </button>
        
        {/* 모바일 드로어 기능이 연계된 네비게이션 메뉴 */}
        <nav className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          {/* 공통 메뉴 */}
          {canAccessMenu('topic-areas') && (
            <Link to="/topic-areas" className={getNavItemClass('/topic-areas')} onClick={() => setIsMobileMenuOpen(false)}>주제영역</Link>
          )}
          
          <Link to="/recommended" className={getNavItemClass('/recommended')} onClick={() => setIsMobileMenuOpen(false)}>추천타임라인</Link>
          
          {canAccessMenu('timeline') && (
            <Link to="/timeline" className={getNavItemClass('/timeline')} onClick={() => setIsMobileMenuOpen(false)}>타임라인</Link>
          )}
          
          {/* 기록 아카이브 */}
          {canAccessMenu('content-create') && (
            <Link to="/archives" className={getNavItemClass('/archives')} onClick={() => setIsMobileMenuOpen(false)}>기록 아카이브</Link>
          )}
          
          {/* 게시판 메뉴 */}
          {canAccessMenu('notice') && (
            <Link to="/boards/notice" className={getNavItemClass('/boards/notice')} onClick={() => setIsMobileMenuOpen(false)}>공지</Link>
          )}
          {canAccessMenu('free') && (
            <Link to="/boards/free" className={getNavItemClass('/boards/free')} onClick={() => setIsMobileMenuOpen(false)}>자유</Link>
          )}
          {canAccessMenu('debate') && (
            <Link to="/boards/debate" className={getNavItemClass('/boards/debate')} onClick={() => setIsMobileMenuOpen(false)}>토론</Link>
          )}
          
          {/* 로그인한 사용자 공통 메뉴 */}
          {isLogin && (
            <>
              {canAccessMenu('notifications') && (
                <Link to="/notifications" className={getNavItemClass('/notifications')} onClick={() => setIsMobileMenuOpen(false)}>알림</Link>
              )}
              {canAccessMenu('mypage') && (
                <Link to="/mypage" className={getNavItemClass('/mypage')} onClick={() => setIsMobileMenuOpen(false)}>마이페이지</Link>
              )}
            </>
          )}
          
          {/* 테마 토글 버튼 */}
          <button 
            type="button" 
            onClick={toggleTheme} 
            className="theme-toggle-btn" 
            aria-label="테마 전환"
            title={theme === 'light' ? '다크 모드로 전환' : '라이트 모드로 전환'}
          >
            {theme === 'light' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            )}
          </button>

          {/* 인증 관련 메뉴 */}
          {isLogin ? (
            <>
              {canAccessMenu('profile') && (
                <Link to="/profile" className={getNavItemClass('/profile')} onClick={() => setIsMobileMenuOpen(false)}>내 정보</Link>
              )}
              <button 
                onClick={async () => { 
                  setIsMobileMenuOpen(false);
                  try {
                    const response = await axiosInstance.post('/api/auth/logout');
                    
                    if (response.status === 200) {
                      localStorage.clear();
                      sessionStorage.clear();
                      await refetch();
                      window.location.href = '/';
                    } else {
                      console.error('Logout failed');
                    }
                  } catch (err) {
                    console.error('Logout error:', err);
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/';
                  }
                }} 
                className="logout-btn"
              >
                로그아웃
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={getNavItemClass('/login')} onClick={() => setIsMobileMenuOpen(false)}>로그인</Link>
              <Link to="/register" className={getNavItemClass('/register', 'auth-btn-register')} onClick={() => setIsMobileMenuOpen(false)}>회원가입</Link>
            </>
          )}
          
          {/* 관리자 전용 메뉴 */}
          {canAccessMenu('admin') && (
            <Link to="/admin" className={getNavItemClass('/admin', 'admin-item')} onClick={() => setIsMobileMenuOpen(false)}>운영자</Link>
          )}
          {canAccessMenu('dashboard') && (
            <Link to="/dashboard" className={getNavItemClass('/dashboard', 'admin-item')} onClick={() => setIsMobileMenuOpen(false)}>대시보드</Link>
          )}
          {canAccessMenu('category-manage') && (
            <Link to="/category-manage" className={getNavItemClass('/category-manage', 'admin-item')} onClick={() => setIsMobileMenuOpen(false)}>카테고리 관리</Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
